/**
 * CycleReportDialog — admin report: every client × recurring form (Plan of Care) cycle status.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClipboardList, Download, Loader2, PenLine, RefreshCw } from "lucide-react";
import { fmtDate } from "./assessmentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = {
  overdue: ["Overdue", "bg-rose-100 text-rose-800 border-rose-200"],
  due_soon: ["Due soon", "bg-amber-100 text-amber-800 border-amber-200"],
  in_progress: ["Update in progress", "bg-sky-100 text-sky-800 border-sky-200"],
  ok: ["On track", "bg-emerald-100 text-emerald-800 border-emerald-200"],
  not_started: ["Original not signed", "bg-slate-100 text-slate-700 border-slate-200"],
  complete: ["Cycle complete", "bg-violet-100 text-violet-800 border-violet-200"],
  superseded: ["Replaced by new cycle", "bg-slate-100 text-slate-500 border-slate-200"],
};

export default function CycleReportDialog({ onClose, canEdit, onOpenDraft, onRevise }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [busy, setBusy] = useState(null);

  const load = () => axios.get(`${API}/assessments/cycle-report`, { params: { status } }).then((r) => setData(r.data)).catch(() => toast.error("Could not load the cycle report"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/assessments/cycle-report`, { params: { status, format: "csv" }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `plan-of-care-cycles.csv` });
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const revise = async (row) => {
    setBusy(row.root_id);
    try {
      const r = await axios.post(`${API}/assessments/${row.latest_id}/revise`);
      toast.success(`${row.template_title} — ${r.data.revision_label} started for ${row.client_name}`);
      onRevise?.(r.data);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not start the update"); } finally { setBusy(null); }
  };

  const s = data?.summary || {};
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col" data-testid="cycle-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><ClipboardList className="w-4 h-4 text-rose-600" /> Plan of Care cycle report</DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {Object.entries(STATUS).map(([k, [label, cls]]) => (
            <button key={k} type="button" onClick={() => setStatus(status === k ? "all" : k)} className={`rounded-full border px-2 py-0.5 ${cls} ${status === k ? "ring-2 ring-slate-400" : ""}`} data-testid={`cycle-filter-${k}`}>
              {label}: <b>{data ? (status === "all" || status === k ? s[k] ?? 0 : "–") : "…"}</b>
            </button>
          ))}
          <span className="ml-auto text-slate-500 hidden md:inline">Updates every {data?.interval_days ?? 60} days · warning {data?.due_soon_days ?? 7} days ahead</span>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-7 w-44 text-xs" data-testid="cycle-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All statuses</SelectItem>
              {Object.entries(STATUS).map(([k, [label]]) => <SelectItem key={k} value={k} className="text-xs">{label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={exportCsv} data-testid="cycle-export-csv"><Download className="w-3.5 h-3.5" /> CSV</Button>
        </div>
        <div className="flex-1 overflow-auto rounded-md border">
          {!data ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : data.rows.length === 0 ? (
            <p className="p-6 text-sm text-slate-500 italic text-center" data-testid="cycle-empty">No recurring forms yet — turn on “Update at visits” on the Plan of Care template in Settings → Form Templates.</p>
          ) : (
            <table className="w-full text-xs" data-testid="cycle-report-table">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr>{["Client", "Form", "Progress", "Last visit", "Next update due", "Status", ""].map((h) => <th key={h} className="text-left font-medium px-3 py-2">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y">
                {data.rows.map((r) => {
                  const [label, cls] = STATUS[r.status] || [r.status, ""];
                  return (
                    <tr key={r.root_id} className="hover:bg-slate-50" data-testid={`cycle-row-${r.root_id}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{r.client_name}{!r.client_active && <span className="ml-1 text-[10px] text-slate-400">(inactive)</span>}</td>
                      <td className="px-3 py-2 text-slate-700">{r.template_title}<div className="text-[10px] text-slate-400">cycle started {fmtDate(r.cycle_started)}</div></td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-20 rounded bg-slate-200 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, (r.updates_done / r.updates_total) * 100)}%` }} /></div>
                          <span data-testid={`cycle-progress-${r.root_id}`}>{r.updates_done} / {r.updates_total}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{r.last_visit_date ? fmtDate(r.last_visit_date) : "—"}<div className="text-[10px] text-slate-400">{r.last_rn}</div></td>
                      <td className="px-3 py-2 text-slate-700">
                        {r.next_due ? <>{fmtDate(r.next_due)} <span className={`text-[10px] ${r.days_left < 0 ? "text-rose-600" : "text-slate-400"}`}>({r.days_left < 0 ? `${-r.days_left} d overdue` : `in ${r.days_left} d`})</span><div className="text-[10px] text-slate-400">Update {r.next_revision} of {r.updates_total}</div></>
                          : r.draft_label ? <span className="text-sky-700">{r.draft_label} open</span> : "—"}
                      </td>
                      <td className="px-3 py-2"><Badge variant="outline" className={`text-[10px] ${cls}`} data-testid={`cycle-status-${r.root_id}`}>{label}</Badge></td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {canEdit && r.draft_id && <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => onOpenDraft?.(r.draft_id)} data-testid={`cycle-continue-${r.root_id}`}><PenLine className="w-3 h-3" /> Continue</Button>}
                        {canEdit && ["overdue", "due_soon", "ok"].includes(r.status) && (
                          <Button size="sm" className="h-7 gap-1 text-xs bg-sky-600 hover:bg-sky-700" disabled={busy === r.root_id} onClick={() => revise(r)} data-testid={`cycle-revise-${r.root_id}`}>
                            {busy === r.root_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Start update
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
